import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { guid, iCaseCompare, NotifyService } from '@fry/lib/utils';
import { BookingItem, BookingItemsStore } from '@fry/booking/lib/booking-items';
import {
  EditorEligibility
} from '@fry/booking/eligibility-editor/editor-eligibility/editor-eligibility.model';
import { EditorWorkflow } from '@fry/booking/workflow-editor/lib/editor-workflow.model';

import {
  EditorBookingItem,
  SETTING_ELIGIBILITY_INDEX,
  SETTING_WORKFLOW_INDEX
} from '../editor-booking-item/editor-booking-item.model';
import {
  EditorBookingItemMetadata
} from '../editor-booking-item-metadata/editor-booking-item-metadata.model';
import {
  EditorBookingItemSetting
} from '../editor-booking-item-setting/editor-booking-item-setting.model';
import { EditorBookingItemTree } from './editor-booking-item-tree.model';
import { EditorBookingItemTreeService } from './editor-booking-item-tree.service';
import {
  EditorBookingItemBulkName
} from '../editor-booking-item-bulk-name-edit/editor-booking-item-bulk-name-edit.store';
import {
  EditorBookingItemSettingEligibility
} from '../editor-booking-item-setting/editor-booking-item-setting-eligibility.model';
import {
  EditorBookingItemSettingWorkflow
} from '../editor-booking-item-setting/editor-booking-item-setting-workflow.model';


@Injectable()
export class EditorBookingItemTreeStore {
  private readonly _bookingItem$ = new BehaviorSubject<BookingItem>(null);
  private readonly _itemTree$ = new BehaviorSubject<EditorBookingItemTree>(null);
  private readonly _editingItemTree$ = new BehaviorSubject<EditorBookingItemTree>(null);
  private readonly _editingItem$ = new BehaviorSubject<EditorBookingItem>(null);
  private readonly _parentEditingItem$ = new BehaviorSubject<EditorBookingItem>(null);
  private readonly _editingItemNameList$ = new BehaviorSubject<EditorBookingItem[]>(undefined);

  readonly bookingItem$ = this._bookingItem$.asObservable();
  readonly itemTree$ = this._itemTree$.asObservable();
  readonly editingItemTree$ = this._editingItemTree$.asObservable();
  readonly editingItem$ = this._editingItem$.asObservable();
  readonly parentEditingItem$ = this._parentEditingItem$.asObservable();
  readonly editingItemNameList$ = this._editingItemNameList$.asObservable();
  readonly rootItem$ = this.itemTree$
    .pipe(
      map(itemTree => {
          if (itemTree === null) {
            return null;
          }

          if (itemTree.itemList.length === 0) {
            return null;
          }

          return itemTree.itemList[0];
        }
      )
    );
  readonly itemOptions$ = this.itemTree$
    .pipe(
      map(itemTree => {
          if (itemTree === null) {
            return null;
          }

          if (itemTree.itemList.length === 0) {
            return null;
          }

          return itemTree.itemList.slice(1);
        }
      )
    );

  constructor(
    private notify: NotifyService,
    private bookingItems: BookingItemsStore,
    private itemTreeService: EditorBookingItemTreeService
  ) {
    this.bookingItem$
      .pipe(filter(bookingItem => bookingItem !== null && !bookingItem.isNew()))
      .subscribe(bookingItem => {
        const fltr = {
          omitParts: ['form', 'eligibility'],
          path: []
        };
        this.bookingItems.getOptionsHierarchy(bookingItem.id, fltr).subscribe(bookingOptions => {
          this.itemTree = Object.assign(
            new EditorBookingItemTree(),
            this.itemTree,
            {
              state: bookingItem.workflow.title,
              bookingStatus: bookingOptions.summary.openOptions > 0 ? $localize `OPEN` : $localize `CLOSED`
            }
          );
        });
        this.bookingItems.describe(bookingItem.id).subscribe(integrationInfo => {
          this.itemTree = Object.assign(
            new EditorBookingItemTree(),
            this.itemTree,
            { integrationInfo }
          );
        });
      });
  }

  private get bookingItem(): BookingItem {
    return this._bookingItem$.getValue();
  }

  private set bookingItem(value: BookingItem) {
    this._bookingItem$.next(value);
  }

  private get itemTree(): EditorBookingItemTree {
    return this._itemTree$.getValue();
  }

  private set itemTree(value: EditorBookingItemTree) {
    this._itemTree$.next(value);
  }

  private get editingItemTree(): EditorBookingItemTree {
    return this._editingItemTree$.getValue();
  }

  private set editingItemTree(value: EditorBookingItemTree) {
    this._editingItemTree$.next(value);
  }

  private get editingItem(): EditorBookingItem {
    return this._editingItem$.getValue();
  }

  private set editingItem(value: EditorBookingItem) {
    this._editingItem$.next(value);
  }

  private get parentEditingItem(): EditorBookingItem {
    return this._parentEditingItem$.getValue();
  }

  private set parentEditingItem(value: EditorBookingItem) {
    this._parentEditingItem$.next(value);
  }

  private get editingItemNameList(): EditorBookingItem[] {
    return this._editingItemNameList$.getValue();
  }

  private set editingItemNameList(value: EditorBookingItem[]) {
    this._editingItemNameList$.next(value);
  }

  initialize(itemTree: EditorBookingItemTree) {
    this.itemTree = itemTree;
    this.editingItemTree = null;
    this.editingItem = null;
    this.parentEditingItem = null;
    this.editingItemNameList = undefined;
  }

  initializeEmpty(typeId?: string) {
    const bookingItem = new BookingItem({ _id: guid() });
    bookingItem.doc.eligibilities = [];
    bookingItem.doc.workflows = [];
    this.bookingItem = bookingItem;

    const itemTree = this.itemTreeService.getEmptyItemTree(bookingItem);
    if (typeId !== undefined) {
      itemTree.bookingType = typeId;
    }
    this.initialize(itemTree);
  }

  initializeFromBookingItem(bookingItem: BookingItem) {
    this.bookingItem = bookingItem;
    this.initialize(this.itemTreeService.getItemTreeFromBookingItem(bookingItem));
  }

  getBookingItemFromItemTree(): BookingItem {
    return this.itemTreeService.getBookingItemFromItemTree(this.bookingItem, this.itemTree);
  }

  save() {
    const newBookingItem = this.itemTreeService.getBookingItemFromItemTree(this.bookingItem, this.itemTree);
    this.bookingItems.createOrSave(newBookingItem).subscribe(
      bookingItem => {
        this.notify.success($localize `Successfully saved`);
        this.initializeFromBookingItem(bookingItem);
      },
      error => {
        this.notify.error(error);
      }
    );
  }

  publish() {
    const newBookingItem = this.itemTreeService.getBookingItemFromItemTree(this.bookingItem, this.itemTree);
    newBookingItem.publish();
    this.bookingItems.createOrSave(newBookingItem).subscribe(
      bookingItem => {
        this.notify.success($localize `Successfully saved and published`);
        this.bookingItem = bookingItem;
      },
      error => {
        this.notify.error(error);
      }
    );
  }

  archive() {
    const newBookingItem = this.itemTreeService.getBookingItemFromItemTree(this.bookingItem, this.itemTree);
    newBookingItem.archive();
    this.bookingItems.createOrSave(newBookingItem).subscribe(
      bookingItem => {
        this.notify.success($localize `Successfully saved and archived`);
        this.bookingItem = bookingItem;
      },
      error => {
        this.notify.error(error);
      }
    );
  }

  retract() {
    const newBookingItem = this.itemTreeService.getBookingItemFromItemTree(this.bookingItem, this.itemTree);
    newBookingItem.retract();
    this.bookingItems.createOrSave(newBookingItem).subscribe(
      bookingItem => {
        this.notify.success($localize `Successfully saved and moved to draft`);
        this.bookingItem = bookingItem;
      },
      error => {
        this.notify.error(error);
      }
    );
  }

  terminate() {
    this.stopEditing();
    this.itemTree = null;
    this.bookingItem = null;
  }

  stopEditing() {
    this.stopEditingItemTree();
    this.stopEditingItem();
    this.stopEditingNameList();
  }

  startEditingItemTree() {
    this.editingItemTree = this.itemTree;
  }

  stopEditingItemTree() {
    this.editingItemTree = null;
  }

  startEditingItem(itemId: string) {
    const curItemTree = this.itemTree;
    const editingItem = curItemTree.getItemById(itemId);

    this.parentEditingItem = editingItem.parentId !== null ? curItemTree.getItemById(editingItem.parentId) : null;
    this.editingItem = editingItem;
  }

  stopEditingItem() {
    this.editingItem = null;
    this.parentEditingItem = null;
  }

  startEditingNameList(itemId: string) {
    const curItemTree = this.itemTree;
    const itemIndex = curItemTree.getItemIndexById(itemId);
    const nextBranchItemIndex = curItemTree.getNextBranchItemIndex(itemIndex);
    this.editingItemNameList = curItemTree.itemList.slice(itemIndex, nextBranchItemIndex);
  }

  stopEditingNameList() {
    this.editingItemNameList = undefined;
  }

  moveItemUp(itemId: string) {
    const curItemTree = this.itemTree;

    const newItemTree = curItemTree.immutableItemListMoveUp(itemId);

    if (newItemTree === curItemTree) {
      return;
    }

    const itemIndex = newItemTree.getItemIndexById(itemId);
    const parentItemId = newItemTree.itemList[itemIndex].parentId;

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);
    newItemTree.resetBundleForAllChildren(parentItemId);

    this.itemTree = newItemTree;
  }

  moveItemDown(itemId: string) {
    const curItemTree = this.itemTree;

    const newItemTree = curItemTree.immutableItemListMoveDown(itemId);

    if (newItemTree === curItemTree) {
      return;
    }

    const itemIndex = newItemTree.getItemIndexById(itemId);
    const parentItemId = newItemTree.itemList[itemIndex].parentId;

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);
    newItemTree.resetBundleForAllChildren(parentItemId);

    this.itemTree = newItemTree;
  }

  addItem(parentItemId: string): EditorBookingItem {
    const curItemTree = this.itemTree;

    const parentItemIndex = curItemTree.getItemIndexById(parentItemId);
    const item = curItemTree.itemList[parentItemIndex].createChild();

    const childrenItemNames = curItemTree.itemList[parentItemIndex].children.map(nameId =>
      curItemTree.getItemById(nameId).name
    );

    const levelName = curItemTree.itemList[parentItemIndex].level === 0
      ? 'option'
      : 'suboption';

    let itemName: string;
    for (let i = 0; ; i++) {
      itemName = `${curItemTree.itemList[parentItemIndex].name} (${levelName} ${i + 1})`;

      if (!childrenItemNames.includes(itemName)) {
        break;
      }
    }
    item.name = itemName;

    const newItemTree = curItemTree.immutableItemListAddItemToParent(item, parentItemId);

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);

    this.itemTree = newItemTree;

    return item;
  }

  addItemAfter(siblingItemId: string): EditorBookingItem {
    const curItemTree = this.itemTree;

    const siblingItemIndex = curItemTree.getItemIndexById(siblingItemId);
    const parentItemIndex = curItemTree.getItemIndexById(curItemTree.itemList[siblingItemIndex].parentId);
    const item = curItemTree.itemList[parentItemIndex].createChild();

    const childrenItemNames = curItemTree.itemList[parentItemIndex].children.map(nameId =>
      curItemTree.getItemById(nameId).name
    );

    const levelName = curItemTree.itemList[parentItemIndex].level === 0
      ? 'option'
      : 'suboption';

    let itemName: string;
    for (let i = 0; ; i++) {
      itemName = `${curItemTree.itemList[parentItemIndex].name} (${levelName} ${i + 1})`;

      if (!childrenItemNames.includes(itemName)) {
        break;
      }
    }
    item.name = itemName;

    const newItemTree = curItemTree.immutableItemListAddItemAfter(item, siblingItemId);

    const parentItemId = curItemTree.itemList[siblingItemIndex].parentId;

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);

    this.itemTree = newItemTree;

    return item;
  }

  duplicateItem(itemId: string) {
    const curItemTree = this.itemTree;

    const siblingItemIndex = curItemTree.getItemIndexById(itemId);

    const clonedItemList = curItemTree.cloneBranch(itemId);
    const parentItemId = curItemTree.itemList[siblingItemIndex].parentId;
    clonedItemList[0].parentId = parentItemId;

    const newItemTree = curItemTree.immutableItemListAddItemListAfter(clonedItemList, itemId);

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);

    this.itemTree = newItemTree;
  }

  deleteItem(itemId: string) {
    const curItemTree = this.itemTree;

    const itemIndex = curItemTree.getItemIndexById(itemId);
    const parentItemId = curItemTree.itemList[itemIndex].parentId;

    const newItemTree = curItemTree.immutableDeleteItem(itemId);

    newItemTree.resetChildren(parentItemId);
    newItemTree.resetSiblingsForAllChildren(parentItemId);
    newItemTree.resetBundleForAllChildren(parentItemId);

    this.itemTree = newItemTree;
  }

  replaceItemTree(replacingItemTree: EditorBookingItemTree) {
    const curItemTree = this.itemTree;

    this.itemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      bookingType: replacingItemTree.bookingType,
      examType: replacingItemTree.examType,
      risrAssessConnectionId: replacingItemTree.risrAssessConnectionId,
      risrAssessBlueprintId: replacingItemTree.risrAssessBlueprintId,
      risrAssessItemSetGenerationMethod: replacingItemTree.risrAssessItemSetGenerationMethod,
      risrAssessItemSetNumber: replacingItemTree.risrAssessItemSetNumber,
      extraRoles: replacingItemTree.extraRoles
    });
  }

  replaceItem(itemId: string, replacingItem: EditorBookingItem) {
    const curItemTree = this.itemTree;

    const itemIndex = curItemTree.getItemIndexById(itemId);

    const nextBranchItemIndex = curItemTree.getNextBranchItemIndex(itemIndex);

    const toReplaceItemList = curItemTree.itemList.slice(itemIndex, nextBranchItemIndex);
    const newItemTree = new EditorBookingItemTree();

    toReplaceItemList.forEach((toReplaceItem, toReplaceItemIndex) => {
      let parentItem: EditorBookingItem;
      let newName: string;
      let newCanBeBooked: boolean;
      let newHideSpecificBookingSettings: boolean;
      let newItemBundle: string[];
      let newItemSettings: EditorBookingItemSetting[];
      let newItemMetadataList: EditorBookingItemMetadata[];

      if (toReplaceItemIndex === 0) {
        parentItem = toReplaceItem.parentId === null ? null : curItemTree.getItemById(toReplaceItem.parentId);
        newName = replacingItem.name;
        newCanBeBooked = replacingItem.canBeBooked;
        newHideSpecificBookingSettings = replacingItem.hideSpecificBookingSettings;
        newItemBundle = replacingItem.bundle;
        newItemSettings = replacingItem.settings;
        newItemMetadataList = replacingItem.metadataList;
      } else {
        parentItem = newItemTree.getItemById(toReplaceItem.parentId);
        newName = toReplaceItem.name;
        newCanBeBooked = toReplaceItem.canBeBooked;
        newHideSpecificBookingSettings = toReplaceItem.hideSpecificBookingSettings;
        newItemBundle = toReplaceItem.bundle;
        newItemSettings = toReplaceItem.settings;
        newItemMetadataList = toReplaceItem.metadataList;
      }

      newItemTree.itemList.push(Object.assign(new EditorBookingItem(), toReplaceItem, {
        name: newName,
        canBeBooked: newCanBeBooked,
        hideSpecificBookingSettings: newHideSpecificBookingSettings,
        ancestorCanBeBooked: (parentItem === null) ?
          false :
          parentItem.canBeBooked || parentItem.ancestorCanBeBooked,
        bundle: newItemBundle,
        settings: (parentItem === null)
          ? newItemSettings
          : parentItem.getCompleteSettings(newItemSettings),
        metadataList: (parentItem === null)
          ? newItemMetadataList.sort((a, b) => iCaseCompare(a.name, b.name))
          : parentItem.getCompleteMetadataList(newItemMetadataList)
      }));
    });

    this.itemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      itemList: [
        ...curItemTree.itemList.slice(0, itemIndex),
        ...newItemTree.itemList,
        ...curItemTree.itemList.slice(nextBranchItemIndex),
      ]
    });
  }

  setShowSettings(itemId: string, showSettings: boolean) {
    const curItemTree = this.itemTree;

    const itemIndex = curItemTree.getItemIndexById(itemId);

    this.itemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      itemList: [
        ...curItemTree.itemList.slice(0, itemIndex),
        Object.assign(new EditorBookingItem(), curItemTree.itemList[itemIndex], {showSettings}),
        ...curItemTree.itemList.slice(itemIndex + 1),
      ]
    });
  }

  replaceItemNameList(bulkNameList: EditorBookingItemBulkName[]) {
    const curItemTree = this.itemTree;

    const newItemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      itemList: [...curItemTree.itemList]
    });

    bulkNameList.forEach(bulkName => {
      const itemIndex = curItemTree.getItemIndexById(bulkName.id);
      newItemTree.itemList[itemIndex] = Object.assign(
        new EditorBookingItem(),
        newItemTree.itemList[itemIndex],
        { name: bulkName.name }
      );
    });

    this.itemTree = newItemTree;
  }

  addEligibility(eligibility: EditorEligibility) {
    const curBookingItem = this.bookingItem;

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          eligibilities: [...curBookingItem.doc.eligibilities, eligibility]
        }
      }
    );
  }

  replaceEligibility(eligibilityId: string, eligibility: EditorEligibility) {
    const curBookingItem = this.bookingItem;
    const eligibilityIndex = curBookingItem.doc.eligibilities.findIndex(eligibilityTemp => eligibilityTemp.id === eligibilityId);

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          eligibilities: [
            ...curBookingItem.doc.eligibilities.slice(0, eligibilityIndex),
            eligibility,
            ...curBookingItem.doc.eligibilities.slice(eligibilityIndex + 1)
          ]
        }
      }
    );
  }

  deleteEligibility(eligibilityId: string) {
    const curItemTree = this.itemTree;

    this.itemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      itemList: curItemTree.itemList.reduce((newItemList, item) => {
        const setting = item.settings[SETTING_ELIGIBILITY_INDEX] as EditorBookingItemSettingEligibility;

        let newSetting;
        if (setting.id === eligibilityId) {
          newSetting = (item.parentId === null)
            ? new EditorBookingItemSettingEligibility({ inheritableType: 'empty' })
            : newItemList.find(itemTemp => itemTemp.id === item.parentId)
              .settings[SETTING_ELIGIBILITY_INDEX].createChild();
        } else {
          newSetting = (item.parentId === null)
            ? item.settings[SETTING_ELIGIBILITY_INDEX]
            : item.settings[SETTING_ELIGIBILITY_INDEX].createComplete(
              newItemList.find(itemTemp => itemTemp.id === item.parentId).settings[SETTING_ELIGIBILITY_INDEX]
            );
        }

        const newItem = Object.assign(
          new EditorBookingItem(),
          item,
          {
            settings: [
              ...item.settings.slice(0, SETTING_ELIGIBILITY_INDEX),
              newSetting,
              ...item.settings.slice(SETTING_ELIGIBILITY_INDEX + 1)
            ]
          }
        );

        return [...newItemList, newItem];
      }, [])
    });

    const curBookingItem = this.bookingItem;
    const eligibilityIndex = curBookingItem.doc.eligibilities.findIndex(eligibility => eligibility.id === eligibilityId);

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          eligibilities: [
            ...curBookingItem.doc.eligibilities.slice(0, eligibilityIndex),
            ...curBookingItem.doc.eligibilities.slice(eligibilityIndex + 1)
          ]
        }
      }
    );
  }

  addWorkflow(workflow: EditorWorkflow) {
    const curBookingItem = this.bookingItem;

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          workflows: [...curBookingItem.doc.workflows, workflow]
        }
      }
    );
  }

  replaceWorkflow(workflowId: string, workflow: EditorWorkflow) {
    const curBookingItem = this.bookingItem;
    const workflowIndex = curBookingItem.doc.workflows.findIndex(workflowTemp => workflowTemp.id === workflowId);

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          workflows: [
            ...curBookingItem.doc.workflows.slice(0, workflowIndex),
            workflow,
            ...curBookingItem.doc.workflows.slice(workflowIndex + 1)
          ]
        }
      }
    );
  }

  deleteWorkflow(workflowId: string) {
    const curItemTree = this.itemTree;

    this.itemTree = Object.assign(new EditorBookingItemTree(), curItemTree, {
      itemList: curItemTree.itemList.reduce((newItemList, item) => {
        const setting = item.settings[SETTING_WORKFLOW_INDEX] as EditorBookingItemSettingWorkflow;

        let newSetting;
        if (setting.id === workflowId) {
          newSetting = (item.parentId === null)
            ? new EditorBookingItemSettingWorkflow({ inheritableType: 'empty' })
            : newItemList.find(itemTemp => itemTemp.id === item.parentId)
              .settings[SETTING_WORKFLOW_INDEX].createChild();
        } else {
          newSetting = (item.parentId === null)
            ? item.settings[SETTING_WORKFLOW_INDEX]
            : item.settings[SETTING_WORKFLOW_INDEX].createComplete(
              newItemList.find(itemTemp => itemTemp.id === item.parentId).settings[SETTING_WORKFLOW_INDEX]
            );
        }

        const newItem = Object.assign(
          new EditorBookingItem(),
          item,
          {
            settings: [
              ...item.settings.slice(0, SETTING_WORKFLOW_INDEX),
              newSetting,
              ...item.settings.slice(SETTING_WORKFLOW_INDEX + 1)
            ]
          }
        );

        return [...newItemList, newItem];
      }, [])
    });

    const curBookingItem = this.bookingItem;
    const workflowIndex = curBookingItem.doc.workflows.findIndex(workflow => workflow.id === workflowId);

    this.bookingItem = Object.assign(
      new BookingItem(curBookingItem.doc), // TODO: WRONG. Fix how to clone objects.
      curBookingItem,
      {
        _doc: {
          ...curBookingItem.doc,
          workflows: [
            ...curBookingItem.doc.workflows.slice(0, workflowIndex),
            ...curBookingItem.doc.workflows.slice(workflowIndex + 1)
          ]
        }
      }
    );
  }
}
